import {useCallback, useEffect, useRef, useState} from "react"
import {Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material"
import {LocalShipping as ShippingIcon, Refresh as RefreshIcon} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import {useUser} from "../../providers/user-context"
import {eventBus} from "../../event-bus"
import {assignOrderCourier, courierName, getCourierOrder, getCourierProfile, reassignOrderCourier, unassignOrderCourier} from "../../data-tables-distributor/orders/courier-api"
import AssignCourierDialog from "../../dialog/distributor/order/assign-courier-dialog"
import CourierDetailsDialog from "../../courier/courier-details-dialog"
import DeliveryProgressChip from "../../courier/delivery-progress-chip"

const OrderCourierAssignment = ({order, open, onOrderRefresh}) => {
    const {tenantId, tenantType, userRole} = useUser()
    const {t, i18n} = useTranslation()
    const permitted = Boolean(tenantId) && tenantType === "IMPORTER" && ["ADMIN", "OPERATOR", "SYSTEM"].includes(userRole)
    const [snapshot, setSnapshot] = useState(order)
    const [courier, setCourier] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [reassignTarget, setReassignTarget] = useState(null)
    const [detailsOpen, setDetailsOpen] = useState(false)
    const [releaseTarget, setReleaseTarget] = useState(null)
    const [releasing, setReleasing] = useState(false)
    const [releaseError, setReleaseError] = useState("")
    const [released, setReleased] = useState(false)
    const releasePending = useRef(false)
    const request = useRef(null)
    const active = useRef(false)
    const language = i18n.language

    const refresh = useCallback(async () => {
        request.current?.abort()
        const controller = new AbortController()
        request.current = controller
        setLoading(true)
        setError("")
        setCourier(null)
        setDetailsOpen(false)
        try {
            const current = await getCourierOrder(order.orderNumber, language, controller.signal, order.pharmacyId)
            if (controller.signal.aborted) return
            setSnapshot(current)
            onOrderRefresh?.(current)
            const profile = current.courierProfileId ? await getCourierProfile(current.courierProfileId, language, controller.signal) : null
            if (!controller.signal.aborted) setCourier(profile)
        } catch (failure) {
            if (!controller.signal.aborted) setError(failure.response?.data?.message || t("CourierAssignmentLoadFailed"))
        } finally {
            if (!controller.signal.aborted) setLoading(false)
        }
    }, [order.orderNumber, order.pharmacyId, language, t, onOrderRefresh])

    useEffect(() => {
        active.current = open && permitted
        setDialogOpen(false)
        setReassignTarget(null)
        setDetailsOpen(false)
        setReleaseTarget(null)
        setReleasing(releasePending.current)
        setSuccess(false)
        setReleased(false)
        if (active.current) refresh()
        return () => { active.current = false; request.current?.abort() }
    }, [open, permitted, tenantId, userRole, refresh])

    const canAssign = permitted && !loading && !error && snapshot.status === "PROCESSING" && !snapshot.courierProfileId
    const canRelease = permitted && !loading && !error && snapshot.status === "PROCESSING" && Boolean(snapshot.courierAssignmentId)
    const canReassign = canRelease && Boolean(courier) && !releasing
    const targetUnchanged = reassignTarget?.assignmentId === snapshot.courierAssignmentId
    const confirm = async (profile, reason) => {
        if (reassignTarget ? !canReassign || !targetUnchanged : !canAssign) throw new Error(t("CourierAssignmentNotAvailable"))
        try {
            const assignment = reassignTarget
                ? await reassignOrderCourier(reassignTarget.orderId, reassignTarget.assignmentId, profile.id, reason, language)
                : await assignOrderCourier(snapshot.orderId, profile.id, language)
            if (!active.current) return
            setSnapshot(current => ({...current, courierProfileId: profile.id, courierAssignmentId: assignment.id, deliveryStatus: "ASSIGNED"}))
            onOrderRefresh?.({...snapshot, courierProfileId: profile.id, courierAssignmentId: assignment.id, deliveryStatus: "ASSIGNED"})
            setCourier(profile)
            setSuccess(reassignTarget ? "CourierReassignedSuccessfully" : "CourierAssignedSuccessfully")
            setReleased(false)
            setDialogOpen(false)
            setReassignTarget(null)
            eventBus.emit("refreshOrderPage")
        } catch (failure) {
            // Read the winner after a conflict or an ambiguous network failure; never retry the POST automatically.
            if (active.current) await refresh()
            throw failure
        }
    }

    const unassign = async () => {
        if (releasePending.current || !canRelease || releaseTarget.assignmentId !== snapshot.courierAssignmentId) return
        releasePending.current = true; setReleasing(true); setReleaseError("")
        try {
            await unassignOrderCourier(releaseTarget.orderId, releaseTarget.assignmentId, language)
            if (!active.current) return
            setReleaseTarget(null); setSuccess(false); setReleased(true)
            eventBus.emit("refreshOrderPage")
            await refresh()
        } catch (failure) {
            if (active.current) {
                setReleaseError(failure.response?.data?.message || t("CourierUnassignmentFailed"))
                // A released/reassigned group must be reviewed again; never switch this confirmation to a new assignment.
                await refresh()
            }
        } finally {
            releasePending.current = false
            if (active.current) setReleasing(false)
        }
    }

    if (!permitted) return null
    return (
        <Card variant="outlined" sx={{borderRadius: 2, mb: 3}}>
            <CardContent>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2}}>
                    <Typography variant="h6" color="primary" sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <ShippingIcon/>{t("AssignedCourier")}
                    </Typography>
                    <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                        <Button variant="outlined" startIcon={<RefreshIcon/>} aria-label={t("RefreshCourierDelivery")}
                            disabled={loading || releasing || dialogOpen || Boolean(reassignTarget)} onClick={async () => {
                                setSuccess(false); setReleased(false)
                                await refresh()
                                if (active.current) eventBus.emit("refreshOrderPage")
                            }}>{t("Refresh")}</Button>
                        {canAssign && <Button variant="contained" startIcon={<ShippingIcon/>} onClick={() => setDialogOpen(true)}>{t("AssignCourier")}</Button>}
                        {canReassign && <Button variant="contained" disabled={dialogOpen || Boolean(releaseTarget) || Boolean(reassignTarget)}
                            onClick={() => setReassignTarget({orderId: snapshot.orderId, assignmentId: snapshot.courierAssignmentId, courier})}>
                            {t("ReassignCourier")}</Button>}
                        {canRelease && <Button color="warning" variant="outlined" disabled={releasing || Boolean(reassignTarget)} onClick={() => {
                            setReleaseError(""); setReleaseTarget({orderId: snapshot.orderId, assignmentId: snapshot.courierAssignmentId})
                        }}>{t("UnassignCourier")}</Button>}
                    </Box>
                </Box>
                {success && <Alert severity="success" sx={{mb: 2}}>{t(success)}</Alert>}
                {released && <Alert severity="success" sx={{mb: 2}}>{t("CourierUnassignedSuccessfully")}</Alert>}
                {error && <Alert severity="error" action={<Button onClick={refresh}>{t("Retry")}</Button>}>{error}</Alert>}
                {loading ? <CircularProgress size={24} aria-label={t("LoadingCourierAssignment")}/>
                    : courier ? <Box><Button sx={{p: 0, textTransform: "none", fontWeight: 600}} onClick={() => setDetailsOpen(true)}>
                        {courierName(courier)}</Button><Typography variant="body2">{courier.userEmail}</Typography>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1}}>
                            <Typography variant="body2">{t("CourierDeliveryProgressTitle")}</Typography>
                            <DeliveryProgressChip status={snapshot.deliveryStatus}/>
                        </Box></Box>
                        : !error && <Typography color="text.secondary">{t(snapshot.courierProfileId ? "CourierAssignmentLoadFailed" : "NoCourierAssigned")}</Typography>}
            </CardContent>
            {(dialogOpen || reassignTarget) && <AssignCourierDialog open
                onClose={() => {setDialogOpen(false); setReassignTarget(null)}} tenantId={tenantId}
                order={snapshot} canAssign={Boolean(reassignTarget ? canReassign && targetUnchanged : canAssign)} onConfirm={confirm}
                assignedCourier={reassignTarget?.courier || courier} reassign={Boolean(reassignTarget)}/>}
            {detailsOpen && courier && <CourierDetailsDialog key={`${tenantId}:${courier.id}`} id={courier.id}
                onClose={() => setDetailsOpen(false)}/>}
            {releaseTarget && <Dialog open onClose={() => { if (!releasePending.current) setReleaseTarget(null) }}
                disableEscapeKeyDown={releasing} fullWidth maxWidth="sm">
                <DialogTitle>{t("UnassignCourier")}</DialogTitle>
                <DialogContent>
                    <Typography>{t("CourierUnassignConfirmation", {orderNumber: snapshot.orderNumber})}</Typography>
                    {releaseError && <Alert severity="error" sx={{mt: 2}}>{releaseError}</Alert>}
                </DialogContent>
                <DialogActions><Button disabled={releasing} onClick={() => setReleaseTarget(null)}>{t("Cancel")}</Button>
                    <Button color="warning" variant="contained" disabled={releasing || !canRelease
                        || releaseTarget.assignmentId !== snapshot.courierAssignmentId} onClick={unassign}>
                        {t(releasing ? "UnassigningCourier" : "Confirm")}</Button></DialogActions>
            </Dialog>}
        </Card>
    )
}

export default OrderCourierAssignment
